import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactTooltip from "react-tooltip";
import Loader from "../../common/components/Loader";
import "./QueryCreatedUserFilter.css";

const normalizeId = id => (id == null ? "" : String(id));

const DISABLED_TIP = "Created By is available when Favorites is set to All";

const QueryCreatedUserFilter = ({ selectedIds = [], onChange, options = [], isLoading = false, disabled = false, onFetchOptions }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [draftIds, setDraftIds] = useState([]);
    const containerRef = useRef(null);

    const normalizedOptions = useMemo(
        () =>
            (options || [])
                .map(item => ({
                    id: normalizeId(item.id ?? item.user_id),
                    name: item.name,
                    count: item.count ?? item.query_count ?? 0
                }))
                .filter(item => item.id),
        [options]
    );

    const normalizedSelectedIds = useMemo(() => selectedIds.map(normalizeId).filter(Boolean), [selectedIds]);

    const searchList = useMemo(() => {
        if (!search) {
            return normalizedOptions;
        }
        const term = search.toLowerCase();
        return normalizedOptions.filter(item => item.name?.toLowerCase().includes(term));
    }, [normalizedOptions, search]);

    const selectedOptions = normalizedOptions.filter(opt => draftIds.includes(opt.id));
    const availableOptions = searchList.filter(opt => !draftIds.includes(opt.id));
    const isAllSelected = searchList.length > 0 && searchList.every(opt => draftIds.includes(opt.id));
    const isFiltered = normalizedSelectedIds.length > 0;

    useEffect(() => {
        if (!disabled) {
            ReactTooltip.hide();
        }
        const timer = setTimeout(() => ReactTooltip.rebuild(), 50);
        return () => clearTimeout(timer);
    }, [disabled]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        const handleClickOutside = event => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
                setSearch("");
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isOpen]);

    const openDropdown = () => {
        if (disabled) {
            return;
        }
        if (!isOpen) {
            onFetchOptions?.();
            setDraftIds([...normalizedSelectedIds]);
            setSearch("");
        }
        setIsOpen(!isOpen);
    };

    const handleChange = id => {
        setDraftIds(prev => (prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]));
    };

    const handleSelectAll = e => {
        setDraftIds(e.target.checked ? searchList.map(opt => opt.id) : []);
    };

    const handleClearSelection = () => {
        setDraftIds([]);
    };

    const handleOk = () => {
        onChange(draftIds.map(normalizeId).filter(Boolean));
        setIsOpen(false);
        setSearch("");
    };

    const handleCancel = () => {
        setDraftIds([...normalizedSelectedIds]);
        setIsOpen(false);
        setSearch("");
    };

    const renderOption = (item, checked) => (
        <span className="dropdown-item notranslate" key={item.id}>
            <label className="container-check d-flex align-items-center">
                <span className="text-short">{item.name || "-"}</span>
                <span className="count-num"> ({item.count})</span>
                <input type="checkbox" checked={checked} onChange={() => handleChange(item.id)} />
                <span className="checkmark" />
            </label>
        </span>
    );

    return (
        <>
            {disabled && (
                <ReactTooltip
                    id="created-user-filter"
                    effect="solid"
                    place="top"
                    className="rc-tooltip-custom-class rich-text-tooltip"
                    backgroundColor="#007bff"
                    delayShow={100}
                />
            )}
            <div className={`sort-by-fl query-created-user-filter ${disabled ? "query-created-user-filter--disabled" : ""}`} ref={containerRef}>
                <div className="sort-by-innr">
                    <label>Created By</label>
                </div>
                <div className="fl-outs d-flex">
                    <div className="selectOtr">
                        <div className={isOpen ? "dropdown-container dropdown-open" : "dropdown-container"}>
                            <div
                                className={`dropdown-toggle click-dropdown query-created-user-toggle ${isFiltered ? "bg-th-filtered" : ""}`}
                                onClick={openDropdown}
                                {...(disabled ? { "data-tip": DISABLED_TIP, "data-for": "created-user-filter" } : {})}
                            >
                                <span className="text-drp-overflw">Select</span>
                                <i className="fas fa-chevron-down query-created-user-chevron" />
                            </div>
                            {isOpen && (
                                <div className="dropdown-menu dropdown-active">
                                    <div className="drop-filtr query-created-user-panel" onClick={e => e.stopPropagation()}>
                                        {isLoading && !normalizedOptions.length ? (
                                            <div className="query-created-user-panel-loader">
                                                <Loader />
                                            </div>
                                        ) : normalizedOptions.length ? (
                                            <>
                                                <div className="query-created-user-panel-top col-md-12 p-0 slt">
                                                    <span className="dropdown-item build_search_drp">
                                                        <input
                                                            type="search"
                                                            placeholder="Search..."
                                                            value={search}
                                                            onChange={e => setSearch(e.target.value)}
                                                        />
                                                        <i className="fas fa-times cursor-pointer cls-close" onClick={() => setSearch("")} />
                                                        <div className="container-check fil-container">
                                                            <label>
                                                                {`Select All (${selectedOptions.length}/${selectedOptions.length + availableOptions.length})`}
                                                                <input type="checkbox" checked={isAllSelected} onChange={handleSelectAll} />
                                                                <span className="checkmark" />
                                                            </label>
                                                            <button type="button" className="clear-btn-selection" onClick={handleClearSelection}>
                                                                Clear
                                                            </button>
                                                        </div>
                                                    </span>
                                                </div>
                                                <div className="query-created-user-panel-body col-md-12 p-0 slt-ara">
                                                    {selectedOptions.length > 0 && (
                                                        <div className="border-bottom">{selectedOptions.map(item => renderOption(item, true))}</div>
                                                    )}
                                                    {availableOptions.map(item => renderOption(item, false))}
                                                </div>
                                                <div className="col-md-12 drp-btn query-created-user-panel-footer">
                                                    <button type="button" className="btn btn-primary btnRgion mr-2" onClick={handleOk}>
                                                        OK
                                                    </button>
                                                    <button type="button" className="btn btn-primary btnClr" onClick={handleCancel}>
                                                        Cancel
                                                    </button>
                                                </div>
                                            </>
                                        ) : (
                                            <div className="query-created-user-panel-empty">No Data Found</div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default QueryCreatedUserFilter;
